import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Plus, FileText, Image as ImageIcon, MoreHorizontal, X,
  Trash2, Pencil, RefreshCw, Loader2,
} from "lucide-react";
import { api } from "../api/client";
import { toast } from "../hooks/use-toast";
import DocumentViewer from "./DocumentViewer";

const MAX_ATTACHMENTS = 10;
const MAX_SIZE_BYTES = 8 * 1024 * 1024;
const ACCEPT = ".pdf,.jpg,.jpeg,.png,.webp,application/pdf,image/jpeg,image/png,image/webp";
const MIME_BY_EXT = {
  pdf: "application/pdf", jpg: "image/jpeg", jpeg: "image/jpeg",
  png: "image/png", webp: "image/webp",
};

function formatSize(n) {
  if (!n && n !== 0) return "";
  if (n < 1024) return `${n} o`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} Ko`;
  return `${(n / (1024 * 1024)).toFixed(1)} Mo`;
}

function fileToDataURL(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = () => reject(new Error("read"));
    r.readAsDataURL(file);
  });
}

/**
 * Attachments section for an intervention.
 *
 * NO activity_log side-effects: adding / renaming / replacing / deleting
 * a document never writes into the timeline (guaranteed by backend).
 *
 * Props:
 *   interventionId: string
 *   onCountChange?: (count) => void
 */
export function AttachmentsSection({ interventionId, onCountChange }) {
  const [items, setItems] = useState([]);
  const [limit, setLimit] = useState(MAX_ATTACHMENTS);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [menuOpen, setMenuOpen] = useState(null); // aid
  const [renameId, setRenameId] = useState(null);
  const [renameValue, setRenameValue] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(null);
  // In-app viewer state — replaces the old window.open("_blank") flow
  // that opened a Chrome Custom Tab with no way back to the vehicle.
  const [viewer, setViewer] = useState(null); // { src, mimeType, title } | null
  const fileInputRef = useRef(null);
  const replaceInputRef = useRef(null);
  const replaceTargetRef = useRef(null);

  const load = useCallback(async () => {
    if (!interventionId) return;
    try {
      const r = await api.get(`/interventions/${interventionId}/attachments`);
      setItems(r.data.items || []);
      setLimit(r.data.limit || MAX_ATTACHMENTS);
      onCountChange?.(r.data.count || 0);
    } catch (e) {
      // 404 or auth — show empty
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [interventionId, onCountChange]);

  useEffect(() => { load(); }, [load]);

  const onFilesPicked = async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    let currentCount = items.length;
    for (const file of Array.from(files)) {
      if (currentCount >= limit) {
        toast({ title: "Limite de 10 documents atteinte" });
        break;
      }
      if (file.size > MAX_SIZE_BYTES) {
        toast({ title: "Fichier trop volumineux", description: file.name });
        continue;
      }
      const ext = (file.name.split(".").pop() || "").toLowerCase();
      const mime = MIME_BY_EXT[ext];
      if (!mime) {
        toast({ title: "Format non pris en charge", description: file.name });
        continue;
      }
      try {
        const dataUrl = await fileToDataURL(file);
        const resp = await api.post(`/interventions/${interventionId}/attachments`, {
          data_base64: dataUrl,
          filename: file.name,
          mime_type: mime,
          size_bytes: file.size,
        });
        currentCount += 1;
        setItems((prev) => [...prev, resp.data]);
      } catch (e) {
        const msg = e?.response?.data?.detail || "Impossible d'ajouter le document";
        toast({ title: msg, description: file.name });
      }
    }
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
    onCountChange?.(currentCount);
  };

  const onReplacePicked = async (files) => {
    const file = files?.[0];
    const target = replaceTargetRef.current;
    if (!file || !target) return;
    if (file.size > MAX_SIZE_BYTES) {
      toast({ title: "Fichier trop volumineux" });
      return;
    }
    const ext = (file.name.split(".").pop() || "").toLowerCase();
    const mime = MIME_BY_EXT[ext];
    if (!mime) { toast({ title: "Format non pris en charge" }); return; }
    setUploading(true);
    try {
      const dataUrl = await fileToDataURL(file);
      const resp = await api.patch(`/interventions/${interventionId}/attachments/${target}`, {
        data_base64: dataUrl, filename: file.name, mime_type: mime, size_bytes: file.size,
      });
      setItems((prev) => prev.map((it) => (it.id === target ? resp.data : it)));
    } catch (e) {
      toast({ title: e?.response?.data?.detail || "Impossible d'ajouter le document" });
    } finally {
      setUploading(false);
      replaceTargetRef.current = null;
      if (replaceInputRef.current) replaceInputRef.current.value = "";
    }
  };

  const submitRename = async () => {
    const v = (renameValue || "").trim();
    if (!v) { toast({ title: "Nom vide" }); return; }
    try {
      const resp = await api.patch(`/interventions/${interventionId}/attachments/${renameId}`, {
        display_name: v,
      });
      setItems((prev) => prev.map((it) => (it.id === renameId ? resp.data : it)));
      setRenameId(null); setRenameValue("");
    } catch (e) {
      toast({ title: e?.response?.data?.detail || "Impossible d'ajouter le document" });
    }
  };

  const doDelete = async () => {
    const aid = confirmDelete;
    if (!aid) return;
    try {
      await api.delete(`/interventions/${interventionId}/attachments/${aid}`);
      setItems((prev) => prev.filter((it) => it.id !== aid));
      onCountChange?.(Math.max(0, items.length - 1));
    } catch (e) {
      toast({ title: e?.response?.data?.detail || "Impossible d'ajouter le document" });
    } finally {
      setConfirmDelete(null);
    }
  };

  const openFile = (it) => {
    if (!it.data_base64) { toast({ title: "Document indisponible" }); return; }
    setViewer({
      src: it.data_base64,
      mimeType: it.mime_type,
      title: it.display_name || it.filename || "",
    });
  };

  const count = items.length;
  const atLimit = count >= limit;

  return (
    <div data-testid="attachments-section" style={{ marginTop: 8 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
        <div style={{ fontSize: 12, fontWeight: 800, letterSpacing: 0.6, textTransform: "uppercase", color: "var(--mf-text-secondary)" }}>
          Documents · <span data-testid="attachment-count">{count} / {limit}</span>
        </div>
      </div>

      {loading ? (
        <div style={{ padding: 12, color: "var(--mf-text-muted)", fontSize: 13 }}>Chargement…</div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {items.map((it) => (
            <AttachmentRow
              key={it.id} item={it}
              onOpen={() => openFile(it)}
              onMenu={() => setMenuOpen(menuOpen === it.id ? null : it.id)}
              menuOpen={menuOpen === it.id}
              onRename={() => { setRenameId(it.id); setRenameValue(it.display_name || ""); setMenuOpen(null); }}
              onReplace={() => {
                replaceTargetRef.current = it.id;
                setMenuOpen(null);
                replaceInputRef.current?.click();
              }}
              onDelete={() => { setConfirmDelete(it.id); setMenuOpen(null); }}
            />
          ))}

          {atLimit ? (
            <div data-testid="attachment-limit-reached" style={{ padding: "10px 12px", fontSize: 13, color: "var(--mf-text-secondary)", background: "var(--mf-bg-muted)", borderRadius: 12 }}>
              Limite de 10 documents atteinte
            </div>
          ) : (
            <button
              type="button"
              data-testid="attachment-add-btn"
              disabled={uploading}
              onClick={() => fileInputRef.current?.click()}
              style={{
                display: "inline-flex", alignItems: "center", gap: 8,
                padding: "10px 14px", background: "var(--mf-bg-muted)",
                border: "1px dashed var(--mf-border)", borderRadius: 12,
                color: "var(--mf-brand)", fontWeight: 700, fontSize: 13, cursor: "pointer",
              }}
            >
              {uploading ? <Loader2 size={16} className="spin" /> : <Plus size={16} />}
              Ajouter un document
            </button>
          )}

          <input
            ref={fileInputRef} type="file" multiple accept={ACCEPT}
            capture="environment"
            style={{ display: "none" }}
            onChange={(e) => onFilesPicked(e.target.files)}
            data-testid="attachment-file-input"
          />
          <input
            ref={replaceInputRef} type="file" accept={ACCEPT}
            style={{ display: "none" }}
            onChange={(e) => onReplacePicked(e.target.files)}
          />
        </div>
      )}

      {renameId && (
        <div style={overlayStyle} onClick={() => setRenameId(null)}>
          <div style={sheetStyle} onClick={(e) => e.stopPropagation()}>
            <div style={{ fontWeight: 800, marginBottom: 12 }}>Renommer le document</div>
            <input
              autoFocus value={renameValue} onChange={(e) => setRenameValue(e.target.value)}
              maxLength={80}
              data-testid="attachment-rename-input"
              style={{ width: "100%", padding: "10px 12px", border: "1px solid var(--mf-border)", borderRadius: 10, fontSize: 14 }}
            />
            <div style={{ display: "flex", gap: 8, marginTop: 14, justifyContent: "flex-end" }}>
              <button onClick={() => setRenameId(null)} style={btnGhost}>Annuler</button>
              <button onClick={submitRename} data-testid="attachment-rename-save" style={btnPrimary}>Enregistrer</button>
            </div>
          </div>
        </div>
      )}

      {confirmDelete && (
        <div style={overlayStyle} onClick={() => setConfirmDelete(null)}>
          <div style={sheetStyle} onClick={(e) => e.stopPropagation()}>
            <div style={{ fontWeight: 800, marginBottom: 8 }}>Supprimer ce document ?</div>
            <div style={{ fontSize: 13, color: "var(--mf-text-secondary)", marginBottom: 14 }}>
              L&apos;intervention et les autres documents restent inchangés.
            </div>
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button onClick={() => setConfirmDelete(null)} style={btnGhost}>Annuler</button>
              <button onClick={doDelete} data-testid="attachment-delete-confirm" style={{ ...btnPrimary, background: "var(--mf-urgent-solid)" }}>Supprimer</button>
            </div>
          </div>
        </div>
      )}

      <DocumentViewer
        open={!!viewer}
        src={viewer?.src}
        mimeType={viewer?.mimeType}
        title={viewer?.title}
        onClose={() => setViewer(null)}
      />
    </div>
  );
}

function AttachmentRow({ item, onOpen, onMenu, menuOpen, onRename, onReplace, onDelete }) {
  const isPdf = item.mime_type === "application/pdf";
  const Icon = isPdf ? FileText : ImageIcon;
  return (
    <div
      data-testid={`attachment-row-${item.id}`}
      style={{ position: "relative", display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", background: "var(--mf-bg-card)", border: "1px solid var(--mf-border)", borderRadius: 12 }}
    >
      <span style={{ width: 32, height: 32, borderRadius: 8, background: "var(--mf-bg-muted)", display: "inline-flex", alignItems: "center", justifyContent: "center", color: "var(--mf-brand)" }}>
        <Icon size={16} />
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: "var(--mf-text)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {item.display_name}
        </div>
        <div style={{ fontSize: 11, color: "var(--mf-text-muted)" }}>{formatSize(item.size_bytes)}</div>
      </div>
      <button
        onClick={onOpen}
        data-testid={`attachment-open-${item.id}`}
        style={{ padding: "6px 10px", background: "var(--mf-bg-muted)", border: "none", borderRadius: 8, color: "var(--mf-brand)", fontWeight: 700, fontSize: 12, cursor: "pointer" }}
      >
        Ouvrir
      </button>
      <button
        onClick={onMenu}
        data-testid={`attachment-menu-${item.id}`}
        aria-label="Options du document"
        style={{ padding: 6, background: "transparent", border: "none", color: "var(--mf-text-secondary)", cursor: "pointer" }}
      >
        <MoreHorizontal size={18} />
      </button>
      {menuOpen && (
        <div style={{ position: "absolute", top: 46, right: 8, background: "var(--mf-bg-card)", border: "1px solid var(--mf-border)", borderRadius: 10, boxShadow: "var(--mf-shadow-strong)", zIndex: 50, minWidth: 160, overflow: "hidden" }}>
          <MenuItem onClick={onRename} icon={<Pencil size={14} />} label="Renommer" testID={`attachment-rename-${item.id}`} />
          <MenuItem onClick={onReplace} icon={<RefreshCw size={14} />} label="Remplacer" testID={`attachment-replace-${item.id}`} />
          <MenuItem onClick={onDelete} icon={<Trash2 size={14} />} label="Supprimer" testID={`attachment-delete-${item.id}`} danger />
        </div>
      )}
    </div>
  );
}

function MenuItem({ onClick, icon, label, testID, danger }) {
  return (
    <button
      onClick={onClick}
      data-testid={testID}
      style={{
        display: "flex", alignItems: "center", gap: 8, width: "100%",
        padding: "10px 12px", background: "transparent", border: "none",
        color: danger ? "var(--mf-urgent-solid)" : "var(--mf-text)",
        fontWeight: 700, fontSize: 13, textAlign: "left", cursor: "pointer",
      }}
    >
      {icon} {label}
    </button>
  );
}

const overlayStyle = {
  position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)",
  display: "flex", alignItems: "center", justifyContent: "center", zIndex: 60, padding: 20,
};
const sheetStyle = {
  background: "var(--mf-bg-card)", borderRadius: 16, padding: 18, width: "100%", maxWidth: 380,
};
const btnGhost = { padding: "8px 14px", background: "transparent", border: "1px solid var(--mf-border)", borderRadius: 10, fontWeight: 700, fontSize: 13, cursor: "pointer" };
const btnPrimary = { padding: "8px 14px", background: "var(--mf-brand)", color: "#fff", border: "none", borderRadius: 10, fontWeight: 700, fontSize: 13, cursor: "pointer" };
